import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, type PlannerForecastTypeValue} from '../consts'

interface DispatchAutofillError {
  detail?: string
}

interface DispatchAutofillPayload {
  plantCode: string
  config: {
    startTime: string
    endTime: string
    forecastType: PlannerForecastTypeValue
  }
}

const dispatchAutofill = async ({plantCode, config}: DispatchAutofillPayload): Promise<void> => {
  await ApiClient.axiosInstance.post<void>(`/hcem/ui/v2/autofill/${plantCode}`, {
    start_time: config.startTime,
    end_time: config.endTime,
    forecast_type: config.forecastType
  })
}

export const useDispatchAutofill = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<DispatchAutofillError>, DispatchAutofillPayload>({
    mutationFn: dispatchAutofill,
    onError: (error) => {
      spawnErrorToast(t('error.apology'))
      const detail = error.response?.data.detail
      if (typeof detail === 'string') spawnErrorToast(detail)
      else if (detail) raiseError(new Error(JSON.stringify(detail)))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: ['autofillStatus'], refetchType: 'all'})
    },
    onSuccess: () => {
      spawnSuccessToast(t('planning.autofill.autofillStarted'))
    }
  })
}
